import { useMemo } from 'react';
import { useTranslation } from '@shared/core';
import { SortOption } from '@shared/constants/accommodations';

export const useSortOptions = (includeCouplesPicks: boolean, includeFundSavings = false) => {
  // `t` (named keys) + `tq` (tagged) must share one namespace per file (lint:
  // joy/translate-quick-single-namespace), so the cash-fund sort label lives in the console namespace.
  const { t, tq } = useTranslation('accommodationsConsole');
  const sortTrans = t('sort');
  const fundSavingsLabel = tq`Highest Fund Savings`;

  const sortOptionsMap = useMemo(() => {
    const base: ReadonlyArray<readonly [SortOption, string]> = [
      [SortOption.TOP_MATCHES, sortTrans.topMatches()],
      [SortOption.PRICE_LOW_TO_HIGH, sortTrans.priceLowToHigh()],
      [SortOption.PRICE_HIGH_TO_LOW, sortTrans.priceHighToLow()],
      [SortOption.MOST_SAVINGS, sortTrans.mostSavings()],
      [SortOption.DISTANCE, sortTrans.distance()],
    ];
    const withCouples: ReadonlyArray<readonly [SortOption, string]> = includeCouplesPicks ? [[SortOption.COUPLES_PICKS, sortTrans.couplesPicks()], ...base] : base;
    // Cash-fund variant surfaces "Highest Fund Savings" as the first/primary sort.
    const entries: ReadonlyArray<readonly [SortOption, string]> = includeFundSavings ? [[SortOption.HIGHEST_FUND_SAVINGS, fundSavingsLabel], ...withCouples] : withCouples;
    return new Map(entries);
  }, [sortTrans, includeCouplesPicks, includeFundSavings, fundSavingsLabel]);

  const sortOptions = useMemo(() => Array.from(sortOptionsMap.entries()).map(([value, label]) => ({ value, label })), [sortOptionsMap]);

  const getSelectedOptionLabel = (selectedValue: SortOption) => {
    return sortOptionsMap.get(selectedValue) || sortTrans.couplesPicks();
  };

  return {
    sortOptions,
    getSelectedOptionLabel,
    sortTrans,
  };
};
